import React, { useEffect, useMemo, useState } from 'react'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import Divider from '@material-ui/core/Divider'
import { Moment } from 'moment'
import { Options } from 'material-table'

import { ERRORS } from 'consts'
import { useApiCall } from 'hooks'
import { snackbar } from 'components'

import { DefaultTable } from '../../components'
import { getParkingMetricsByBenefit } from './api'
import {
  formatMinutesAsHours,
  formatPercent,
  formatUsages,
  stripHtml,
} from './format'
import { useStyles } from './styles'
import {
  ParkingBenefitMetricRow,
  ParkingMetricsByBenefitResponse,
  ParkingMetricsDateParams,
  ParkingMetricsTotals,
} from './types'

interface Props {
  startDate: Moment
  endDate: Moment
}

interface TableRow {
  id: string | number
  parkingConfigId: string | number
  title: string
  usagesLabel: string
  consumedMinutes: number | null
  grantedMinutes: number | null
  percentLabel: string
  isTotal?: boolean
}

const TOTAL_ROW_ID = '__parking_metrics_total__'

const tightCell = {
  whiteSpace: 'nowrap' as const,
  padding: '0 10px',
  height: 37,
  maxHeight: 37,
  lineHeight: '37px',
  overflow: 'hidden' as const,
  textOverflow: 'ellipsis' as const,
  boxSizing: 'border-box' as const,
}

const tightHeader = {
  whiteSpace: 'nowrap' as const,
  padding: '0 10px',
  height: 40,
  maxHeight: 40,
  lineHeight: '40px',
  boxSizing: 'border-box' as const,
}

const columns = [
  {
    title: 'ID',
    field: 'parkingConfigId',
    width: '8%',
    cellStyle: { ...tightCell, width: '8%', paddingLeft: 16 },
    headerStyle: { ...tightHeader, width: '8%', paddingLeft: 16 },
  },
  {
    title: 'Beneficio',
    field: 'title',
    width: '38%',
    cellStyle: { ...tightCell, width: '38%' },
    headerStyle: { ...tightHeader, width: '38%' },
  },
  {
    title: 'Cantidad de usos',
    field: 'usagesLabel',
    width: '14%',
    cellStyle: { ...tightCell, width: '14%' },
    headerStyle: { ...tightHeader, width: '14%' },
  },
  {
    title: 'Horas consumidas',
    field: 'consumedMinutes',
    width: '14%',
    cellStyle: { ...tightCell, width: '14%' },
    headerStyle: { ...tightHeader, width: '14%' },
    render: (row: TableRow) => formatMinutesAsHours(row.consumedMinutes),
  },
  {
    title: 'Horas otorgadas',
    field: 'grantedMinutes',
    width: '14%',
    cellStyle: { ...tightCell, width: '14%' },
    headerStyle: { ...tightHeader, width: '14%' },
    render: (row: TableRow) => formatMinutesAsHours(row.grantedMinutes),
  },
  {
    title: '% consumidos',
    field: 'percentLabel',
    width: '12%',
    cellStyle: { ...tightCell, width: '12%' },
    headerStyle: { ...tightHeader, width: '12%' },
  },
]

const emptyTotals: ParkingMetricsTotals = {
  usages: 0,
  consumedMinutes: 0,
  grantedMinutes: 0,
}

const getTableOptions = (): Options => ({
  pageSize: 10,
  toolbar: false,
  paging: true,
  sorting: false,
  emptyRowsWhenPaging: false,
  rowStyle: (rowData: TableRow) => {
    if (rowData.isTotal) {
      return {
        height: 37,
        backgroundColor: '#F5F5F5',
        fontWeight: 600,
      }
    }
    return { height: 37 }
  },
  headerStyle: {
    height: 40,
    padding: 0,
  },
})

const ByBenefitTable: React.FC<Props> = ({ startDate, endDate }) => {
  const classes = useStyles()
  const [rows, setRows] = useState<ParkingBenefitMetricRow[]>([])
  const [totals, setTotals] = useState<ParkingMetricsTotals>(emptyTotals)
  const [requestMetrics, isLoading] = useApiCall<
    ParkingMetricsDateParams,
    ParkingMetricsByBenefitResponse
  >(getParkingMetricsByBenefit)

  useEffect(() => {
    requestMetrics({ startDate, endDate })
      .then(resp => {
        setRows(resp?.data || [])
        setTotals(resp?.totals || emptyTotals)
      })
      .catch(() => {
        setRows([])
        setTotals(emptyTotals)
        snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate])

  const tableData: TableRow[] = useMemo(() => {
    const dataRows = rows.map(row => ({
      id: row.parkingConfigId,
      parkingConfigId: row.parkingConfigId,
      title: stripHtml(row.title),
      usagesLabel: formatUsages(row.usages),
      consumedMinutes: row.consumedMinutes,
      grantedMinutes: row.grantedMinutes,
      percentLabel: formatPercent(row.consumedPercent),
    }))

    return [
      ...dataRows,
      {
        id: TOTAL_ROW_ID,
        parkingConfigId: 'Total',
        title: '',
        usagesLabel: formatUsages(totals.usages),
        consumedMinutes: totals.consumedMinutes,
        grantedMinutes: totals.grantedMinutes,
        percentLabel: '',
        isTotal: true,
      },
    ]
  }, [rows, totals])

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="h6" component="h2" className={classes.title}>
          Horas de parking por beneficio
        </Typography>
        <Divider className={classes.divider} />
      </Grid>
      <Grid item xs={12}>
        <div className={classes.tableWrap}>
          <DefaultTable
            title="Parking por beneficio"
            columns={columns}
            data={tableData}
            isLoading={isLoading}
            options={getTableOptions()}
          />
        </div>
      </Grid>
    </>
  )
}

export { ByBenefitTable }
